import React, { useEffect, useState } from "react";
import { ukDay } from "@/lib/time";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyField, PhoneField } from "@/components/ui/number-fields";
import { toast } from "sonner";
import { Link2, Copy, Check, QrCode, Send, Loader2, Plus, Bell, X, ExternalLink, Eye, MousePointerClick, FileCheck2, ChevronDown, BookOpen } from "lucide-react";

const STATUS = {
  sent:      { label: "Sent",      cls: "border-slate-200 bg-slate-50 text-slate-600" },
  opened:    { label: "Opened",    cls: "border-blue-200 bg-blue-50 text-blue-700" },
  started:   { label: "Started",   cls: "border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8]" },
  submitted: { label: "Submitted", cls: "border-teal-200 bg-teal-50 text-teal-700" },
  cancelled: { label: "Cancelled", cls: "border-slate-200 bg-slate-50 text-slate-400" },
};

const fmt = (iso) => ukDay(iso, "—");

/**
 * ApplyLinksPanel — an introducer's own funding front door: a permanent link + QR,
 * plus one-off customer invites tracked Sent → Opened → Started → Submitted.
 */
export function ApplyLinksPanel({ financeTypes = [], campaign = false }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedF, setCopiedF] = useState(false);
  const [qr, setQr] = useState(false);
  const [qrF, setQrF] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [busy, setBusy] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = () => api.get("/refer/apply-link").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (!data?.url) return null;
  const s = data.stats || {};
  const sessions = data.sessions || [];
  const shown = showAll ? sessions : sessions.slice(0, 6);

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.url); setCopied(true); setTimeout(() => setCopied(false), 1600); toast.success("Link copied — paste it anywhere"); }
    catch { toast.error("Copy failed"); }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Apply for business funding", text: "Apply for business funding with Connexis", url: data.url }); return; } catch { /* cancelled */ }
    }
    copy();
  };

  const copyFunding = async () => {
    try { await navigator.clipboard.writeText(data.funding_url); setCopiedF(true); setTimeout(() => setCopiedF(false), 1600); toast.success("Explainer link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const shareFunding = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Business funding, explained", text: "How business funding works with Connexis", url: data.funding_url }); return; } catch { /* cancelled */ }
    }
    copyFunding();
  };

  const act = async (id, what) => {
    setBusy(id + what);
    try {
      await api.post(`/refer/apply-invites/${id}/${what}`);
      toast.success(what === "nudge" ? "Reminder sent" : "Invite cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(null); }
  };

  const downloadQr = () => {
    const canvas = document.querySelector('[data-testid="apply-qr"] canvas');
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "connexis-apply-qr.png";
    a.click();
  };

  const downloadFundingQr = () => {
    const canvas = document.querySelector('[data-testid="apply-funding-qr"] canvas');
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "connexis-funding-explainer-qr.png";
    a.click();
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="apply-links-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">{campaign ? "Your campaign links" : "Send customers an application"}</div>
        {!campaign && (
          <Button onClick={() => setInviting(true)} size="sm" className="h-8 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-[12px] font-semibold" data-testid="apply-invite-open">
            <Plus className="size-3.5 mr-1" /> Send an application
          </Button>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Permanent link */}
        <div>
          <div className="text-[13px] text-slate-500 leading-relaxed">
            This is <b className="text-slate-900">your</b> funding link. Put it on your website, your email signature or a
            WhatsApp — anyone who applies through it is automatically credited to you.
          </div>
          <div className="mt-3 flex items-stretch gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px] rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 font-mono text-[12.5px] text-slate-700 truncate" data-testid="apply-link-url">
              {data.url.replace(/^https?:\/\//, "")}
            </div>
            <Button onClick={copy} variant="outline" className="h-[46px] rounded-xl" data-testid="apply-link-copy">
              {copied ? <><Check className="size-4 mr-1.5 text-teal-500" /> Copied</> : <><Copy className="size-4 mr-1.5" /> Copy</>}
            </Button>
            <Button onClick={share} variant="outline" className="h-[46px] rounded-xl" data-testid="apply-link-share">
              <Send className="size-4 mr-1.5" /> Share
            </Button>
            <Button onClick={() => setQr((q) => !q)} variant="outline" className="h-[46px] rounded-xl" data-testid="apply-link-qr-toggle">
              <QrCode className="size-4 mr-1.5" /> QR
            </Button>
            <a href={data.url} target="_blank" rel="noreferrer"
               className="h-[46px] px-3.5 inline-flex items-center rounded-xl border border-slate-200 text-slate-600 hover:border-slate-300 text-[13px]" data-testid="apply-link-preview">
              <ExternalLink className="size-4" />
            </a>
          </div>

          {qr && (
            <div className="mt-3 rounded-xl border border-slate-200 p-4 flex items-center gap-4 flex-wrap" data-testid="apply-qr">
              <QRCodeCanvas value={data.url} size={132} level="M" includeMargin bgColor="#ffffff" fgColor="#0B1220" />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-900">Print it, stick it anywhere</div>
                <p className="mt-0.5 text-[12px] text-slate-500 max-w-xs leading-relaxed">
                  Business cards, van livery, a shop window, the back of your invoice. It opens your application form.
                </p>
                <Button onClick={downloadQr} size="sm" variant="outline" className="mt-2 h-8 rounded-full text-[11.5px]" data-testid="apply-qr-download">
                  Download PNG
                </Button>
              </div>
            </div>
          )}

          {/* The explainer page, under their own name */}
          {data.funding_url && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="apply-funding-link">
              <div className="text-[13px] font-semibold text-slate-900">Not ready to apply yet? Send them this first</div>
              <p className="mt-0.5 text-[12px] text-slate-500 leading-relaxed">
                The same page we'd send a nervous customer — who we are, what it costs them (nothing), how the first
                48 hours go — with <b className="text-slate-700">"sent to you by you"</b> at the top and every button
                leading to your form.
              </p>
              <div className="mt-2.5 flex items-stretch gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px] rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 font-mono text-[12px] text-slate-700 truncate"
                     data-testid="apply-funding-url">
                  {data.funding_url.replace(/^https?:\/\//, "")}
                </div>
                <Button onClick={copyFunding} variant="outline" className="h-[46px] rounded-xl bg-white" data-testid="apply-funding-copy">
                  {copiedF ? <><Check className="size-4 mr-1.5 text-teal-500" /> Copied</> : <><Copy className="size-4 mr-1.5" /> Copy</>}
                </Button>
                <Button onClick={shareFunding} variant="outline" className="h-[46px] rounded-xl bg-white" data-testid="apply-funding-share">
                  <Send className="size-4 mr-1.5" /> Share
                </Button>
                <Button onClick={() => setQrF((q) => !q)} variant="outline" className="h-[46px] rounded-xl bg-white" data-testid="apply-funding-qr-toggle">
                  <QrCode className="size-4 mr-1.5" /> QR
                </Button>
                <a href={data.funding_url} target="_blank" rel="noreferrer"
                   className="h-[46px] px-3.5 inline-flex items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                   data-testid="apply-funding-preview">
                  <ExternalLink className="size-4" />
                </a>
              </div>

              {qrF && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4 flex-wrap" data-testid="apply-funding-qr">
                  <QRCodeCanvas value={data.funding_url} size={124} level="M" includeMargin bgColor="#ffffff" fgColor="#0B1220" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-900">For leaflets and windows</div>
                    <p className="mt-0.5 text-[12px] text-slate-500 max-w-xs leading-relaxed">
                      Scanning it opens the explainer page in your name, with your application form one tap away.
                    </p>
                    <Button onClick={downloadFundingQr} size="sm" variant="outline" className="mt-2 h-8 rounded-full text-[11.5px]"
                            data-testid="apply-funding-qr-download">
                      Download PNG
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Stat Icon={BookOpen} label="Explainer" value={s.funding_views} />
            <Stat Icon={Eye} label="Link views" value={s.views} />
            <Stat Icon={Send} label="Invites sent" value={s.invites_sent} />
            <Stat Icon={MousePointerClick} label="Started" value={s.started} />
            <Stat Icon={FileCheck2} label="Submitted" value={s.submitted} tone />
          </div>
        </div>

        {/* Tracker */}
        {sessions.length > 0 && (
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-slate-400">Tracking</div>
            <div className="mt-2 divide-y divide-slate-100" data-testid="apply-tracker">
              {shown.map((x) => {
                const st = STATUS[x.status] || STATUS.sent;
                return (
                  <div key={x.id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`apply-track-${x.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-slate-900 truncate">
                        {x.customer_name || (campaign ? "An applicant via your campaign" : "Someone on your link")}
                        {x.kind === "public" && !campaign && <span className="ml-1.5 text-[10.5px] text-slate-400">via your link</span>}
                      </div>
                      <div className="text-[11.5px] text-slate-400 truncate">
                        {campaign
                          ? (x.reference ? `Ref ${x.reference} · details held by Connexis` : "Details held by Connexis")
                          : <>{[x.customer_email, x.customer_phone].filter(Boolean).join(" · ") || "no contact details yet"}{x.reference ? ` · ${x.reference}` : ""}</>}
                      </div>
                    </div>
                    <span className={`inline-flex items-center rounded-full border px-2 h-5 text-[10.5px] font-semibold ${st.cls}`}>{st.label}</span>
                    <span className="text-[11px] text-slate-400 tabular-nums w-14 text-right">{fmt(x.submitted_at || x.started_at || x.opened_at || x.sent_at || x.created_at)}</span>
                    {x.kind === "invite" && !["submitted", "cancelled"].includes(x.status) && (
                      <span className="inline-flex gap-1">
                        <button onClick={() => act(x.id, "nudge")} disabled={busy === x.id + "nudge"}
                                className="grid place-items-center size-8 rounded-lg border border-slate-200 text-slate-500 hover:border-slate-300" title="Send a reminder"
                                data-testid={`apply-nudge-${x.id}`}>
                          {busy === x.id + "nudge" ? <Loader2 className="size-3.5 animate-spin" /> : <Bell className="size-3.5" />}
                        </button>
                        <button onClick={() => act(x.id, "cancel")} disabled={busy === x.id + "cancel"}
                                className="grid place-items-center size-8 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200" title="Cancel this invite"
                                data-testid={`apply-cancel-${x.id}`}>
                          <X className="size-3.5" />
                        </button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {sessions.length > 6 && (
              <button onClick={() => setShowAll((v) => !v)} className="mt-2 text-[12px] text-[#0055FF] font-medium inline-flex items-center gap-1" data-testid="apply-tracker-more">
                {showAll ? "Show less" : `Show all ${sessions.length}`} <ChevronDown className={`size-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        )}
      </div>

      <InviteDialog open={inviting} onOpenChange={setInviting} financeTypes={financeTypes} onSent={load} />
    </div>
  );
}

function Stat({ Icon, label, value, tone }) {
  return (
    <div className={`rounded-xl border p-2.5 ${tone ? "border-teal-200 bg-teal-50/60" : "border-slate-200 bg-slate-50/60"}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-400">
        <Icon className="size-3" /> {label}
      </div>
      <div className={`mt-0.5 text-[19px] font-semibold tabular-nums ${tone ? "text-teal-700" : "text-slate-900"}`}>{value ?? 0}</div>
    </div>
  );
}

function InviteDialog({ open, onOpenChange, financeTypes, onSent }) {
  const [f, setF] = useState({ customer_name: "", customer_email: "", customer_phone: "", finance_type: "", amount_gbp: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const send = async () => {
    if (f.customer_name.trim().length < 2) return toast.error("Add your customer's name");
    if (!f.customer_email.trim() && !f.customer_phone.trim()) return toast.error("Add an email or a mobile so we can send the link");
    setBusy(true);
    try {
      const r = await api.post("/refer/apply-invites", {
        customer_name: f.customer_name.trim(),
        customer_email: f.customer_email.trim() || null,
        customer_phone: f.customer_phone.trim() || null,
        finance_type: f.finance_type || null,
        amount_gbp: f.amount_gbp === "" ? null : Number(f.amount_gbp),
        note: f.note.trim() || null,
      });
      const via = r.data?.sent_via || {};
      toast.success(`Application link sent${via.email && via.sms ? " by email and text" : via.sms ? " by text" : " by email"}`);
      setF({ customer_name: "", customer_email: "", customer_phone: "", finance_type: "", amount_gbp: "", note: "" });
      onOpenChange(false);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send that"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="apply-invite-dialog">
        <DialogHeader>
          <DialogTitle>Send an application</DialogTitle>
          <DialogDescription>
            We'll email and text your customer their own private link. You'll see it move from Sent to Submitted right here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Customer name</Label>
            <Input className="mt-1.5" value={f.customer_name} onChange={(e) => set("customer_name")(e.target.value)}
                   placeholder="Jane Smith" data-testid="apply-invite-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Email</Label>
              <Input className="mt-1.5" type="email" value={f.customer_email} onChange={(e) => set("customer_email")(e.target.value)}
                     placeholder="jane@company.co.uk" data-testid="apply-invite-email" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Mobile</Label>
              <div className="mt-1.5"><PhoneField value={f.customer_phone} onChange={set("customer_phone")} testId="apply-invite-phone" /></div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Finance type <span className="normal-case tracking-normal text-slate-400 font-normal">(optional)</span></Label>
              <Select value={f.finance_type} onValueChange={set("finance_type")}>
                <SelectTrigger className="mt-1.5" data-testid="apply-invite-type"><SelectValue placeholder="Pre-fill for them" /></SelectTrigger>
                <SelectContent className="max-h-64">{financeTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Amount <span className="normal-case tracking-normal text-slate-400 font-normal">(optional)</span></Label>
              <div className="mt-1.5"><MoneyField value={f.amount_gbp} onChange={set("amount_gbp")} ladder="deal" testId="apply-invite-amount" /></div>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Note to the desk <span className="normal-case tracking-normal text-slate-400 font-normal">(optional, they won't see it)</span></Label>
            <Textarea className="mt-1.5" rows={2} value={f.note} onChange={(e) => set("note")(e.target.value)}
                      placeholder="Long-standing client, needs it before the quarter end" data-testid="apply-invite-note" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="apply-invite-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] font-semibold" data-testid="apply-invite-send">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-3.5 mr-1.5" /> Send the link</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
